import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';

import { IPolicyDriver, IAutoQuote, IDriver } from '../../interfaces';

export * from './getDriverAgeConfig';

export function getQuoteDriversProps(quote: IAutoQuote) {
  const driversListWithExcludedDrivers = quote?.application?.drivers || [];
  const activeDriversList = driversListWithExcludedDrivers.filter(
    (driver) => !driver.is_excluded
  );
  const excludedDriversList = driversListWithExcludedDrivers.filter(
    (driver) => driver.is_excluded
  );

  const hasSpouseBeenSet = !!find(driversListWithExcludedDrivers, {
    relationship_to_insured: 'spouse',
  });

  const suggestedDrivers = (
    quote?.application?.metadata?.suggested_drivers || []
  ).filter((suggestedDriver) => {
    return !find(driversListWithExcludedDrivers, {
      id: suggestedDriver.id,
    });
  });

  return {
    driversListWithExcludedDrivers,
    activeDriversList,
    hasSpouseBeenSet,
    suggestedDrivers,
    excludedDriversList,
  };
}

export function getPolicyDriversProps(drivers: IPolicyDriver[]) {
  const sortedDriversByRole = sortBy(drivers, (driver) => {
    if (driver.role === 'pni') {
      return -1;
    }
    if (driver.role === 'driver') {
      return 0;
    }
    return 1;
  }).filter((driver) => !driver.deleted);

  const hasSpouseBeenSet = !!sortedDriversByRole.find(
    (driver) => driver.relationship_to_insured === 'spouse'
  );

  return {
    sortedDriversByRole,
    hasSpouseBeenSet,
  };
}

export function getPolicyDriverProps(driver?: IPolicyDriver) {
  const isPNIDriver = driver?.role === 'pni' || false;
  const isAdpf = driver?.data_source === 'adpf' || false;
  const isRemovable = !isPNIDriver && !!driver;
  const isDriverSavedToPolicy = driver && !!driver.id;
  const isSavedDriverAChild =
    (isDriverSavedToPolicy && driver?.relationship_to_insured === 'child') ||
    false;
  const wasSavedAsDriver =
    (isDriverSavedToPolicy && driver?.role === 'driver') || false;

  return {
    isPNIDriver,
    isAdpf,
    isRemovable,
    isDriverSavedToPolicy,
    isSavedDriverAChild,
    wasSavedAsDriver,
  };
}

export function getDriverAgePropsBasedOnDob(
  dob: string,
  policyRenewsAt?: string | null
) {
  const isYoungerThan23 = !!dob && moment.utc().diff(dob, 'years') < 23;
  const is18orYounger =
    !!dob && !!policyRenewsAt
      ? moment.utc(policyRenewsAt).diff(dob, 'years') < 19
      : moment.utc().diff(dob, 'years') < 19;

  return {
    isYoungerThan23,
    is18orYounger,
  };
}

type TGetRelationshipOptions = {
  isPNI: boolean;
  hasSpouseBeenSet: boolean;
  isSavedDriverAChild?: boolean;
  isDriverSavedToPolicy?: boolean;
  isDriverSpouse?: boolean;
};
export function getRelationshipOptions({
  isPNI,
  hasSpouseBeenSet,
  isSavedDriverAChild = false,
  isDriverSavedToPolicy = false,
  isDriverSpouse = false,
}: TGetRelationshipOptions) {
  if (isPNI) {
    return [
      {
        value: 'self',
        label: 'Self',
      },
    ];
  }
  if (hasSpouseBeenSet && !isDriverSpouse) {
    return [
      {
        value: 'child',
        label: 'Child',
      },
      {
        value: 'other',
        label: 'Other',
      },
    ];
  }
  if (isDriverSavedToPolicy) {
    if (isSavedDriverAChild) {
      return [
        {
          value: 'child',
          label: 'Child',
        },
      ];
    }
    return [
      {
        value: 'spouse',
        label: 'Spouse',
      },
      {
        value: 'other',
        label: 'Other',
      },
    ];
  }
  return [
    {
      value: 'spouse',
      label: 'Spouse',
    },
    {
      value: 'child',
      label: 'Child',
    },
    {
      value: 'other',
      label: 'Other',
    },
  ];
}

type TGetDriversBlacklist = {
  driversList?: (IDriver | IPolicyDriver)[];
  currentDriver?: (IDriver | IPolicyDriver) | null;
};
const DUPLICATE_DRIVER_ERROR_MSG =
  'This drivers license is already associated with your quote.';
export function getDriversBlacklist(
  { driversList, currentDriver }: TGetDriversBlacklist,
  quote?: IAutoQuote
) {
  const drivers = (driversList || quote?.application?.drivers || []).filter(
    (driver) => driver.role !== 'non_driver' && !driver.is_excluded
  );
  const driversLicenseBlacklist = filter(
    drivers.map((driver) => {
      return {
        licenseNumber: driver?.driving_license_number,
        licenseState: driver?.driving_license_state,
      };
    }),
    ({ licenseNumber, licenseState }) =>
      licenseNumber !== currentDriver?.driving_license_number ||
      licenseState !== currentDriver?.driving_license_state
  );

  function isDuplicateDriversLicense(
    licenseNumber: string,
    licenseState: string
  ) {
    return !!find(driversLicenseBlacklist, { licenseNumber, licenseState });
  }

  return { isDuplicateDriversLicense, DUPLICATE_DRIVER_ERROR_MSG };
}

export function getNonDriverReasonOptions(
  is18orYounger: boolean,
  wasSavedAsDriver: boolean = false,
  isNonDriverSpouseOrOther: boolean = false
) {
  return compact([
    !wasSavedAsDriver && {
      value: 'was_never_licensed',
      label: 'Never licensed',
    },
    {
      value: 'is_out_of_country',
      label: 'Out of the country',
    },
    is18orYounger &&
      !wasSavedAsDriver &&
      !(is18orYounger && isNonDriverSpouseOrOther) && {
        value: 'has_learners_permit',
        label: 'Learners permit only',
      },
  ]);
}

export function getInitialNonDriverReason({
  has_learners_permit,
  was_never_licensed,
  is_out_of_country,
}: IDriver | IPolicyDriver) {
  if (has_learners_permit) {
    return 'has_learners_permit';
  }
  if (was_never_licensed) {
    return 'was_never_licensed';
  }
  if (is_out_of_country) {
    return 'is_out_of_country';
  }
  return '';
}

function isPolicyDriver(
  driver: IDriver | IPolicyDriver
): driver is IPolicyDriver {
  return (driver as IPolicyDriver).person !== undefined;
}

export function getDriverId(driver: IDriver | IPolicyDriver) {
  if (driver.id) {
    return driver.id;
  }
  if (driver.role === 'driver') {
    return `${driver.driving_license_number}_${driver.driving_license_state}`;
  }
  const { first_name, last_name, dob } = isPolicyDriver(driver)
    ? driver.person
    : driver;
  return `${first_name}_${last_name}_${moment(dob).format('YYYY-MM-DD')}`;
}
